import React, { useEffect, useState } from "react";
import { Star, Trophy, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const certificate = async (tier) => {
  try {
    const { data } = await api.get(`/me/certificate/${tier}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = Object.assign(document.createElement("a"), { href: url, download: `jt-hub-${tier}-trophy-certificate.pdf` });
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch { toast.error("Couldn't download the certificate. Please try again."); }
};

const when = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const TIERS = [["bronze", 10, "#CD7F32"], ["silver", 50, "#C0C7D0"], ["gold", 100, "#F5B301"]];

// Bronze, silver and gold trophies at 10, 50 and 100 stars, with progress to the next one.
const Trophies = ({ stars }) => {
  const next = TIERS.find(([, n]) => stars < n);
  return (
    <div className="mt-4" data-testid="perfect-trophies">
      <div className="flex flex-wrap gap-3">
        {TIERS.map(([name, n, color]) => {
          const won = stars >= n;
          return (
            <div key={name} data-testid={`trophy-${name}`} data-won={won}
              className={`flex items-center gap-2 rounded-2xl border px-4 py-2 transition-transform ${won ? "border-transparent bg-background shadow-lg hover:scale-105" : "border-dashed border-border opacity-45 grayscale"}`}>
              <Trophy size={26} style={{ color }} className={won ? "drop-shadow" : ""} />
              <div className="leading-tight">
                <p className="text-sm font-bold capitalize">{name}</p>
                <p className="text-[11px] text-muted-foreground">{won ? "Won!" : `${n} stars`}</p>
                {won && (
                  <button type="button" onClick={() => certificate(name)} data-testid={`trophy-${name}-certificate`}
                    className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"><Download size={11} /> Certificate</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {next ? (
        <div className="mt-3 max-w-sm" data-testid="perfect-trophy-next">
          <p className="text-xs text-muted-foreground">{next[1] - stars} more star{next[1] - stars === 1 ? "" : "s"} for the {next[0]} trophy</p>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-border"><div className="h-full rounded-full" style={{ width: `${(stars / next[1]) * 100}%`, background: next[2] }} /></div>
        </div>
      ) : <p className="mt-3 text-xs font-semibold text-primary" data-testid="perfect-trophy-next">You've won every trophy. Amazing!</p>}
    </div>
  );
};

// Dashboard card: one star for every perfect round in any game or quiz.
export const PerfectStarsCard = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/me/perfect").then(({ data: d }) => setData(d)).catch(() => setData({ stars: 0, recent: [], by_game: [] })); }, []);
  if (!data) return null;
  return (
    <section className="mt-10 rounded-3xl border border-border bg-card p-6" data-testid="perfect-stars-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-serif text-2xl"><Star size={20} className="fill-primary text-primary" /> Perfect score stars</h2>
        <p className="flex items-center gap-2 rounded-full bg-primary/15 px-4 py-1.5 font-serif text-2xl text-primary" data-testid="perfect-stars-count">
          <Star size={20} className="fill-primary" /> {data.stars}
        </p>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Get every answer right in a quiz or game round and you'll hear the fanfare and earn a star.</p>
      <Trophies stars={data.stars} />
      {!data.stars ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="perfect-stars-empty">No stars yet. Try a lesson quiz or a game round!</p>
      ) : (
        <div className="mt-4 grid gap-6 md:grid-cols-2">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Stars by game</p>
            <ul className="mt-2 space-y-1 text-sm" data-testid="perfect-stars-by-game">
              {data.by_game.map((g) => (
                <li key={g.game} className="flex justify-between gap-3 border-t border-border py-1.5"><span>{g.game}</span><span className="flex items-center gap-1 font-semibold text-primary"><Star size={12} className="fill-primary" /> {g.stars}</span></li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Latest perfect rounds</p>
            <ul className="mt-2 space-y-1 text-sm" data-testid="perfect-stars-recent">
              {data.recent.slice(0, 10).map((r, i) => (
                <li key={`${r.at}-${i}`} className="flex justify-between gap-3 border-t border-border py-1.5"><span>{r.game}</span><span className="text-xs text-muted-foreground">{when(r.at)}</span></li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
};
